import { useCallback } from 'react'
import { CalendarEvent } from '@highlight/common'
import { Toast, useToast } from '../../index'
import { Button } from '@highlight/ui'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useOpenBrief } from '@renderer/overlay/Sidepanel/components/meeting/brief/use-open-brief'
import { BriefPreview } from '@renderer/overlay/Sidepanel/components/meeting/brief/components/brief-preview'

interface MeetingViewToastProps {
  meeting: CalendarEvent
  toastId: string
}

export function MeetingViewToast({ meeting, toastId }: MeetingViewToastProps) {
  const toast = useToast()
  const openBrief = useOpenBrief()

  const handleViewClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!meeting.calendarEventLink) return

      try {
        const url = new URL(meeting.calendarEventLink)
        if (meeting.userEmail && url.hostname.includes('google.com')) {
          url.searchParams.set('authuser', meeting.userEmail)
        }
        window.open(url.toString(), '_blank', 'noopener,noreferrer')
      } catch {
        window.open(meeting.calendarEventLink, '_blank', 'noopener,noreferrer')
      }

      AmplitudeRenderer.trackEvent('Calendar Event Action', {
        action: 'View Event',
        trigger: 'prepare-toast',
      })
      toast.dismiss(toastId)
    },
    [meeting.calendarEventLink, meeting.userEmail, toast, toastId],
  )

  const onReadBrief = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      AmplitudeRenderer.trackEvent('Meeting Brief', {
        action: 'view',
        source: 'meeting-view-toast',
        meeting_id: meeting.id,
      })
      openBrief({
        calendarEventId: meeting.id,
        isActive: false,
        title: meeting.title,
        meeting,
      })
      toast.dismiss(toastId)
    },
    [openBrief, meeting, toast, toastId],
  )

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        {meeting.brief && (
          <BriefPreview
            brief={meeting.brief.brief}
            relevantLinks={meeting.brief.relevantLinks}
            onClick={onReadBrief}
            disableLinks
          />
        )}
        <Toast.Footer>
          <span>Starts in ~10 minutes</span>
          <Button onClick={handleViewClick} size="sm" variant="outline">
            View
          </Button>
        </Toast.Footer>
      </Toast.Content>
    </Toast>
  )
}
